import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { motion } from "framer-motion";
import Arrow from "../../images/arrow.svg";
import useIsMobile from "../../hooks/useIsMobile";
import { trackScrollToPricing, trackSignUpClick } from "../../utils/analytics";

interface PlansTextInfoProps {
  scrollToPricing: () => void;
}

const PlansTextInfo = ({ scrollToPricing }: PlansTextInfoProps) => {
  const isMobile = useIsMobile();

  const handleScrollToPricing = () => {
    trackScrollToPricing();
    scrollToPricing();
  };

  const handleSignUpClick = () => {
    trackSignUpClick("hero_section");
    window.location.href = "/cadastro";
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <Typography
          letterSpacing="0.1px"
          fontWeight={600}
          lineHeight={1.1}
          color="#22303E"
          maxWidth={isMobile ? "100%" : "400px"}
          fontFamily="sans-serif"
          fontSize={isMobile ? "28px" : "40px"}
          textAlign={isMobile ? "center" : "left"}
          mx={isMobile ? "auto" : 0}
          component="h1"
        >
          Sua quadra lotada, sem o telefone tocar a cada 5 minutos
        </Typography>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.3 }}
      >
        <Typography
          color="#6B7280"
          fontWeight={500}
          maxWidth={isMobile ? "90%" : "360px"}
          fontFamily="sans-serif"
          fontSize={isMobile ? "18px" : "20px"}
          marginTop="20px"
          textAlign={isMobile ? "center" : "left"}
          mx={isMobile ? "auto" : 0}
          component="p"
        >
          Chega de perder tempo com o WhatsApp e caderno. Com a Anota Reservas,
          seu cliente agenda sozinho e você só se preocupa em gerenciar seu
          negócio.
        </Typography>
      </motion.div>

      {/* Social Proof */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.5 }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent={isMobile ? "center" : "left"}
          marginTop="30px"
          gap="10px"
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            {[...Array(5)].map((_, i) => (
              <span key={i} style={{ color: "#FFD700", fontSize: "20px" }}>
                ★
              </span>
            ))}
          </Box>
          <Typography color="#6B7280" fontSize="14px" fontWeight={500}>
            4.8/5
          </Typography>
        </Box>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.6 }}
      >
        <Box
          display="flex"
          marginTop="40px"
          justifyContent={isMobile ? "center" : "left"}
          flexDirection={isMobile ? "column" : "row"}
          gap={isMobile ? "15px" : "20px"}
          width={isMobile ? "100%" : "auto"}
        >
          {/* Primary CTA */}
          <Button
            onClick={handleScrollToPricing}
            variant="contained"
            sx={{
              textTransform: "none",
              fontSize: isMobile ? "14px" : "16px",
              padding: isMobile ? "12px 24px" : "14px 32px",
              borderRadius: "30px",
              background: "linear-gradient(to right, #226FE2, #0033FF)",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              cursor: "pointer",
              boxShadow: "0 4px 14px rgba(34, 111, 226, 0.3)",
              "&:hover": {
                background: "linear-gradient(to right, #1a5bb8, #0026cc)",
                transform: "translateY(-2px)",
                boxShadow: "0 6px 20px rgba(34, 111, 226, 0.4)",
              },
              transition: "all 0.3s ease",
            }}
            aria-label="Confira nossos planos"
            role="button"
          >
            Confira nossos planos
            <img
              src={Arrow}
              width={isMobile ? "16px" : "20px"}
              alt="Seta indicando mais informações sobre os planos"
            />
          </Button>

          {/* Secondary CTA */}
          <Button
            onClick={handleSignUpClick}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontSize: isMobile ? "14px" : "16px",
              padding: isMobile ? "12px 24px" : "14px 32px",
              borderRadius: "30px",
              border: "2px solid #226FE2",
              color: "#226FE2",
              fontWeight: 600,
              cursor: "pointer",
              "&:hover": {
                background: "#226FE2",
                color: "#fff",
                border: "2px solid #226FE2",
                transform: "translateY(-2px)",
              },
              transition: "all 0.3s ease",
            }}
            aria-label="Começar teste grátis"
            role="button"
          >
            Teste grátis agora
          </Button>
        </Box>
      </motion.div>

      {/* Trust indicators */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.8 }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent={isMobile ? "center" : "left"}
          marginTop="30px"
          gap="20px"
          flexWrap="wrap"
        >
          <Box display="flex" alignItems="center" gap="8px">
            <span style={{ fontSize: "16px" }}>🔒</span>
            <Typography fontSize="12px" color="#6B7280">
              Pagamento seguro
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap="8px">
            <span style={{ fontSize: "16px" }}>⚡</span>
            <Typography fontSize="12px" color="#6B7280">
              Setup em 5 min
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap="8px">
            <span style={{ fontSize: "16px" }}>🎯</span>
            <Typography fontSize="12px" color="#6B7280">
              7 dias grátis
            </Typography>
          </Box>
        </Box>
      </motion.div>
    </>
  );
};

export default PlansTextInfo;
